<template>
  <div
    v-if="buttonCoord"
    :class="['label-positioner-outer', { sticky }]"
    :style="sticky ? null : { top: `${buttonCoord.y}%`, left: `${buttonCoord.x}%` }"
    @click.stop="void 0"
  >
    <div class="label-positioner-inner">
      <div
        ref="controlsRef"
        class="label-controls"
        tabindex="0"
        data-threed-entity="label-controls"
        @pointerleave="capturePointer"
        @pointerdown="dragging = true"
        @pointerup.stop="dragging = false"
      >
        <button
          class="flagpole-toggle box-border border-r border-transparent"
          :title="`${!selectedLabel?.flagpoleVisible ? 'Show' : 'Hide'} flagpole`"
          :class="
            selectedLabel && selectedLabel.flagpoleVisible
              ? 'bg-copper-800 hover_bg-copper-900'
              : 'bg-gray-900 hover_bg-gray-800 border-gray-800'
          "
          @click="handleFlagpoleToggle"
        >
          <FlagpoleIcon width="18" height="17" />
        </button>
        <div
          class="flex box-border border-x border-transparent bg-gray-900 px-3 py-2 w-32"
          :class="{ 'rounded-r-md': !userState.flags.AUTOSCALE_3D_LABELS_ENABLED }"
        >
          <EditorNumericStepper
            v-if="selectedLabel && !selectedLabel.autoScale"
            :value="selectedLabel.textSize"
            :step="10"
            @input="handleSizeChange"
          />
          <EditorFontSizeSelector v-else-if="selectedLabel" :value="selectedLabel.autoScaleSize" @input="handleSizeChange" />
        </div>
        <button
          v-if="userState.flags.AUTOSCALE_3D_LABELS_ENABLED"
          :title="`${!selectedLabel?.autoScale ? 'Enable' : 'Disable'} autoscale`"
          class="flex text-xs items-center justify-center gap-2 text-white rounded-r-md px-4 border-l border-transparent box-border"
          :class="
            selectedLabel && !!selectedLabel.autoScale
              ? 'bg-copper-800 hover_bg-copper-900'
              : 'bg-gray-900 hover_bg-gray-800 border-gray-800'
          "
          @click="handleAutoScaleToggle"
        >
          <span class="text-sm overflow-visible rounded-none"><FontAwesomeIcon icon="up-right-and-down-left-from-center" /></span>
          Autoscale on zoom
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Vector3 } from 'three'
import type { PropType } from 'vue'
import { computed, defineComponent, nextTick, onMounted, ref, toRef, watch } from 'vue'

import FlagpoleIcon from '~/components/icons/FlagpoleIcon.svg'
import type { LabelsManager, TextLabel } from '~/lib/three-d-resources/labels-manager'
import { clampSize } from '~/lib/constants/labels'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  selectedLabelId: {
    type: String as PropType<string>,
    required: true,
  },
  labelsManager: {
    type: Object as PropType<LabelsManager>,
    required: true,
  },
})

defineEmits(['toggle-flagpole', 'update:label-size'])

const selectedLabel = ref<TextLabel | null>(null)
const selectedLabelId = toRef(props, 'selectedLabelId')
const controlsRef = ref<HTMLElement | null>(null)
const dragging = ref(false)
const { state: userState } = useUser()

const buttonCoord = computed(() => {
  if (!props.labelsManager.labelsSpec.value || !props.selectedLabelId) return

  const renderedSelectedLabel = props.labelsManager.labelsSpec.value.find(label => label.id === props.selectedLabelId)

  if (!renderedSelectedLabel) return null

  const totalVertices = renderedSelectedLabel.meshVertices.length
  const topCentre = renderedSelectedLabel.meshVertices[totalVertices * (3 / 4)]?.clone()

  if (!topCentre) return null

  // convert to %
  topCentre.add(new Vector3(1, 1)).multiplyScalar(50)
  return topCentre
})

const sticky = computed(() => {
  const coords = buttonCoord.value
  return coords && (coords.x > 95 || coords.x < 5 || coords.y > 100 || coords.y < 10)
})

watch(
  selectedLabelId,
  labelId => {
    const label = props.labelsManager.getLabel(labelId)
    if (label) updateComponent({ label })
  },
  { immediate: true, flush: 'post' }
)

function updateComponent({ label }: { label: TextLabel }) {
  if (label.id === props.selectedLabelId) {
    selectedLabel.value = label
  }
}

async function handleSizeChange(newSize: number) {
  if (!props.selectedLabelId || !selectedLabel.value) return

  const textSize = clampSize(newSize)

  // We are maintaining our own selectedLabel instead of passing via prop
  const { autoScale } = selectedLabel.value

  if (autoScale) {
    props.labelsManager.updateLabel(props.selectedLabelId, { autoScaleSize: textSize })
    selectedLabel.value.autoScaleSize = textSize
  } else {
    props.labelsManager.updateLabel(props.selectedLabelId, { textSize })
    selectedLabel.value.textSize = textSize
  }
}

async function handleFlagpoleToggle() {
  if (!props.selectedLabelId || !selectedLabel.value) return

  const flagpoleVisible = !selectedLabel.value.flagpoleVisible

  props.labelsManager.updateLabel(props.selectedLabelId, { flagpoleVisible, refreshKey: selectedLabel.value.refreshKey })
  selectedLabel.value.flagpoleVisible = flagpoleVisible
  await nextTick()

  _focusOnEditableLabel()
}

async function handleAutoScaleToggle() {
  if (!selectedLabel.value) return
  const autoScale = !selectedLabel.value.autoScale

  props.labelsManager.updateLabel(props.selectedLabelId, { autoScale })
  selectedLabel.value.autoScale = autoScale
}

function _focusOnEditableLabel() {
  const editableElement = document.querySelector<HTMLElement>('.text-label .selected .editable')

  if (!editableElement) return

  editableElement.focus()
  const lastNode = editableElement.childNodes.length
  document.getSelection()?.setPosition(editableElement, lastNode)
}

onMounted(() => {
  const label = props.labelsManager.getLabel(props.selectedLabelId)

  if (label) updateComponent({ label })
})

function capturePointer(e: PointerEvent): void {
  if (dragging.value) {
    controlsRef.value?.setPointerCapture(e.pointerId)
  }
}
</script>

<style lang="stylus" scoped>
.svg-inline--fa
  overflow: visible !important;

.label-controls
  @apply h-10 flex pointer-events-auto
  margin-left -50%

  & > :first-child
    @apply rounded-l-md flex items-center justify-center

  .flagpole-toggle
    @apply w-12 h-full flex-shrink-0

.label-positioner-outer
  @apply absolute w-0 h-0
  &.sticky
    // refers to the bottom of the controls + 0.5rem bottom positioning
    // 4.5rem header + 2.5rem controls height + 0.5rem bottom positioning + 1rem top margin = 8.5rem
    top 8.5rem
    left 50%
    transition-property top left
    transition-duration 150ms
    transition-timing-function ease-out

.label-positioner-inner
  @apply absolute bottom-2 pointer-events-none z-70

.flagpole-toggle
  @apply cursor-pointer w-10 h-10 p-2 text-white text-center
</style>
